import {
    ComponentChildren, h, cloneElement, VNode, Component, createRef, Ref
} from "preact";

import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";

import { SectionHeaderElement, SectionHeaderProps } from "./SectionHeader";

import styles from "./Section.module.css";

interface Props {
    class?: string;
    style?: CSSProperties;
    // Section
    id: string;
    emptyText?: string;
    header?: VNode<SectionHeaderProps>;
    children?: ComponentChildren;
    // Flags
    collapsed?: boolean;
    highlighted?: boolean;
    isDefault?: boolean;
    shouldHighlightHeader?: boolean;
    // Refs
    sectionBaseRef?: Ref<HTMLDivElement>;
    // Callbacks
    onCollapse: (id: string) => void;
    onExpand: (id: string) => void;
    onHighlightEnd: () => void;
}

interface SectionElement {
    highlightName: () => void;
    highlightDescription: () => void;
}

class Section extends Component<Props> implements SectionElement {
    static override defaultProps: Partial<Props> = {
        shouldHighlightHeader: true
    };

    sectionRef = createRef<HTMLElement>();
    sectionHeaderRef = createRef<SectionHeaderElement>();

    highlighting = false;

    public highlightName = () => {
        this.sectionHeaderRef.current!.highlightName();
    };

    public highlightDescription = () => {
        const { id, collapsed, onExpand } = this.props;

        if (collapsed) {
            onExpand(id);
        }

        setTimeout(this.sectionHeaderRef.current!.highlightDescription, 4);
    };

    handleRef = (el: HTMLDivElement | null) => {
        const { sectionBaseRef } = this.props;

        if (typeof sectionBaseRef === "function") {
            sectionBaseRef(el);
        } else if (sectionBaseRef) {
            sectionBaseRef.current = el;
        }

        this.sectionRef.current = el;
    };

    handleHighlight = () => {
        const { highlighted, shouldHighlightHeader } = this.props;
        if (!highlighted) {
            return;
        }

        this.highlighting = true;
        this.sectionRef.current?.scrollIntoView();

        if (shouldHighlightHeader) {
            this.sectionHeaderRef.current?.startHighlightAnimation();
        }

        setTimeout(this.handleHighlightEnd);
    };

    handleHighlightEnd = () => {
        this.highlighting = false;
        this.props.onHighlightEnd();
    };

    handleToggle = () => {
        const { id, collapsed, onExpand, onCollapse } = this.props;

        if (collapsed) {
            onExpand(id);
            return;
        }

        onCollapse(id);
    };

    override componentDidMount() {
        this.handleHighlight();
    }

    override componentDidUpdate() {
        if (!this.highlighting) {
            this.handleHighlight();
        }
    }

    override componentWillUnmount() {
        if (this.highlighting) {
            this.handleHighlightEnd();
        }
    }

    renderHeader = () => {
        const { isDefault, header } = this.props;

        if (isDefault || !header) {
            return null;
        }

        return cloneElement(header, { sectionHeaderRef: this.sectionHeaderRef, onToggle: this.handleToggle });
    };

    render() {
        const {
            class: cssClass,
            style = {},
            children,
            emptyText,
            collapsed
        } = this.props;

        return (
            <div
                ref={this.handleRef}
                class={classname(styles.section, cssClass)}
                style={style}>
                {this.renderHeader()}
                {!collapsed && !emptyText && children}
            </div>
        );
    }
}

export default Section;
export {
    Props as SectionProps,
    SectionElement
};
